import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Package, Plus, Search, AlertTriangle, Calendar, 
  ArrowUp, ArrowDown, Edit, Trash2, X, Check, 
  Loader2, RefreshCw, Filter, Clock, Scan
} from 'lucide-react';
import BarcodeScanner from './BarcodeScanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MEDICATION_TYPES = [
  { value: 'collirio', label: 'Collirio' },
  { value: 'pomata', label: 'Pomata' },
  { value: 'compressa', label: 'Compressa' },
  { value: 'iniezione', label: 'Iniezione' },
  { value: 'dispositivo', label: 'Dispositivo Medico' },
  { value: 'altro', label: 'Altro' }
];

const DEFAULT_CATEGORIES = [
  'Antiglaucoma',
  'Lacrime artificiali',
  'Antibiotici',
  'Antinfiammatori',
  'Midriatici',
  'Anestetici',
  'Dispositivi medici',
  'Altro'
];

export const InventoryManager = () => {
  const [items, setItems] = useState([]);
  const [alerts, setAlerts] = useState({ low_stock_count: 0, expiring_soon_count: 0 });
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showMovementModal, setShowMovementModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterLowStock, setFilterLowStock] = useState(false);
  const [filterExpiring, setFilterExpiring] = useState(false);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [alertsData, setAlertsData] = useState(null);
  const [showAlertsModal, setShowAlertsModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [scannerMode, setScannerMode] = useState('search'); // 'search', 'load', 'unload'
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    medication_type: 'collirio',
    category: '',
    description: '',
    manufacturer: '',
    unit: 'pz',
    min_stock_level: 5,
    price_per_unit: '',
    storage_location: '',
    requires_refrigeration: false,
    notes: ''
  });
  
  const [movementData, setMovementData] = useState({
    item_id: '',
    movement_type: 'in',
    quantity: 1,
    lot_number: '',
    expiry_date: '',
    reason: '',
    supplier: '',
    unit_price: ''
  });

  useEffect(() => {
    loadInventory();
    loadCategories();
  }, [filterCategory, filterLowStock, filterExpiring, searchQuery]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const loadInventory = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCategory) params.append('category', filterCategory);
      if (filterLowStock) params.append('low_stock', 'true');
      if (filterExpiring) params.append('expiring_soon', 'true');
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await axios.get(
        `${API}/hr/inventory?${params.toString()}`,
        getAuthHeaders()
      );
      setItems(response.data.items || []);
      setAlerts(response.data.alerts || { low_stock_count: 0, expiring_soon_count: 0 });
    } catch (error) {
      console.error('Error loading inventory:', error);
      toast.error('Errore nel caricamento del magazzino');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/hr/inventory/categories`, getAuthHeaders());
      if (response.data.categories?.length > 0) {
        setCategories(response.data.categories);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadAlerts = async () => {
    try {
      const response = await axios.get(`${API}/hr/inventory/alerts`, getAuthHeaders());
      setAlertsData(response.data);
      setShowAlertsModal(true);
    } catch (error) {
      toast.error('Errore nel caricamento degli alert');
    }
  };

  // Cerca prodotto via barcode/codice
  const handleBarcodeScan = async (code) => {
    try {
      const response = await axios.get(`${API}/hr/inventory/barcode/${code}`, getAuthHeaders());
      const item = response.data.item;
      
      if (scannerMode === 'search') {
        // Evidenzia il prodotto trovato
        setSearchQuery(item.name);
        toast.success(`Trovato: ${item.name}`);
      } else if (scannerMode === 'load') {
        // Apri modal carico
        openMovementModal(item, 'in');
        toast.success(`Carico: ${item.name}`);
      } else if (scannerMode === 'unload') {
        // Apri modal scarico
        openMovementModal(item, 'out');
        toast.success(`Scarico: ${item.name}`);
      }
      
      setShowScanner(false);
    } catch (error) {
      toast.error(`Prodotto non trovato con codice: ${code}`);
    }
  };

  const openScannerFor = (mode) => {
    setScannerMode(mode);
    setShowScanner(true);
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/hr/inventory`, formData, getAuthHeaders());
      toast.success('Prodotto aggiunto al magazzino');
      setShowAddModal(false);
      resetForm();
      loadInventory();
    } catch (error) {
      const errorMsg = typeof error.response?.data?.detail === 'string' 
        ? error.response.data.detail 
        : 'Errore nell\'aggiunta del prodotto';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateItem = async (e) => {
    e.preventDefault();
    if (!selectedItem) return;
    setSaving(true);
    try {
      await axios.put(`${API}/hr/inventory/${selectedItem.id}`, formData, getAuthHeaders());
      toast.success('Prodotto aggiornato');
      setShowAddModal(false);
      setSelectedItem(null);
      resetForm();
      loadInventory();
    } catch (error) {
      const errorMsg = typeof error.response?.data?.detail === 'string' 
        ? error.response.data.detail 
        : 'Errore nell\'aggiornamento';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo prodotto?')) return;
    try {
      await axios.delete(`${API}/hr/inventory/${itemId}`, getAuthHeaders());
      toast.success('Prodotto eliminato');
      loadInventory();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleMovement = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...movementData,
        quantity: parseInt(movementData.quantity),
        unit_price: movementData.unit_price ? parseFloat(movementData.unit_price) : null
      };
      
      const response = await axios.post(`${API}/hr/inventory/movement`, payload, getAuthHeaders());
      toast.success(`Movimento registrato. Nuova giacenza: ${response.data.new_stock}`);
      setShowMovementModal(false);
      resetMovementForm();
      loadInventory();
    } catch (error) {
      const errorMsg = typeof error.response?.data?.detail === 'string' 
        ? error.response.data.detail 
        : 'Errore nel movimento';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (item) => {
    setSelectedItem(item);
    setFormData({
      name: item.name || '',
      code: item.code || '',
      medication_type: item.medication_type || 'collirio',
      category: item.category || '',
      description: item.description || '',
      manufacturer: item.manufacturer || '',
      unit: item.unit || 'pz',
      min_stock_level: item.min_stock_level || 5,
      price_per_unit: item.price_per_unit || '',
      storage_location: item.storage_location || '',
      requires_refrigeration: item.requires_refrigeration || false,
      notes: item.notes || ''
    });
    setShowAddModal(true);
  };

  const openMovementModal = (item, type = 'in') => {
    setMovementData({
      item_id: item.id,
      movement_type: type,
      quantity: 1,
      lot_number: '',
      expiry_date: '',
      reason: '',
      supplier: '',
      unit_price: ''
    });
    setSelectedItem(item);
    setShowMovementModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      medication_type: 'collirio',
      category: '',
      description: '',
      manufacturer: '',
      unit: 'pz',
      min_stock_level: 5,
      price_per_unit: '',
      storage_location: '',
      requires_refrigeration: false,
      notes: ''
    });
  };

  const resetMovementForm = () => {
    setMovementData({
      item_id: '',
      movement_type: 'in',
      quantity: 1,
      lot_number: '',
      expiry_date: '',
      reason: '',
      supplier: '',
      unit_price: ''
    });
    setSelectedItem(null);
  };

  const getStockStatus = (item) => {
    if (item.current_stock <= 0) return { color: 'bg-red-100 text-red-800', label: 'Esaurito' };
    if (item.current_stock <= item.min_stock_level) return { color: 'bg-yellow-100 text-yellow-800', label: 'Scorta bassa' };
    return { color: 'bg-green-100 text-green-800', label: 'Disponibile' };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT');
  };

  return (
    <div className="space-y-6" data-testid="inventory-manager">
      {/* Header con Alert */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Magazzino Farmaci</h2>
          <p className="text-gray-500">Gestione inventario e scorte</p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          {(alerts.low_stock_count > 0 || alerts.expiring_soon_count > 0) && (
            <button
              onClick={loadAlerts}
              className="flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 transition-colors"
              data-testid="alerts-button"
            >
              <AlertTriangle className="w-4 h-4" />
              {alerts.low_stock_count > 0 && <span>{alerts.low_stock_count} scorte basse</span>}
              {alerts.expiring_soon_count > 0 && <span>{alerts.expiring_soon_count} in scadenza</span>}
            </button>
          )}
          
          <button
            onClick={() => { setSelectedItem(null); resetForm(); setShowAddModal(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors"
            data-testid="add-product-button"
          >
            <Plus className="w-4 h-4" />
            Nuovo Prodotto
          </button>
        </div>
      </div>

      {/* Scanner Barcode Buttons */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-gray-700">📷 Scanner Barcode:</span>
          <button
            onClick={() => openScannerFor('search')}
            className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            data-testid="scan-search-button"
          >
            <Scan className="w-4 h-4" />
            Cerca Prodotto
          </button>
          <button
            onClick={() => openScannerFor('load')}
            className="flex items-center gap-2 px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
            data-testid="scan-load-button"
          >
            <ArrowDown className="w-4 h-4" />
            Carico Rapido
          </button>
          <button
            onClick={() => openScannerFor('unload')}
            className="flex items-center gap-2 px-3 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors"
            data-testid="scan-unload-button"
          >
            <ArrowUp className="w-4 h-4" />
            Scarico Rapido
          </button>
        </div>
        
        {/* Scanner Component */}
        {showScanner && (
          <div className="mt-4">
            <BarcodeScanner 
              onScan={handleBarcodeScan}
              onClose={() => setShowScanner(false)}
              placeholder={
                scannerMode === 'search' ? "Scansiona per cercare..." :
                scannerMode === 'load' ? "Scansiona per caricare..." :
                "Scansiona per scaricare..."
              }
            />
          </div>
        )}
      </div>

      {/* Filtri */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Cerca prodotto..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
              data-testid="search-input"
            />
          </div>
          
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500"
            data-testid="category-filter"
          >
            <option value="">Tutte le categorie</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          
          <label className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={filterLowStock}
              onChange={(e) => setFilterLowStock(e.target.checked)}
              className="rounded text-sky-600"
            />
            <span className="text-sm">Solo scorte basse</span>
          </label>
          
          <label className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={filterExpiring}
              onChange={(e) => setFilterExpiring(e.target.checked)}
              className="rounded text-sky-600"
            />
            <span className="text-sm">In scadenza</span>
          </label>
          
          <button
            onClick={loadInventory}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Lista Prodotti */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">Nessun prodotto trovato</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="mt-4 text-sky-600 hover:underline"
            >
              Aggiungi il primo prodotto
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="inventory-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prodotto</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Giacenza</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Stato</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Scadenze</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map(item => {
                  const status = getStockStatus(item);
                  const nextExpiry = item.expiry_dates?.sort((a, b) => a.expiry?.localeCompare(b.expiry))?.[0];
                  
                  return (
                    <tr key={item.id} className="hover:bg-gray-50" data-testid={`inventory-item-${item.id}`}>
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-gray-900">{item.name}</p>
                          {item.code && <p className="text-xs text-gray-500">Cod: {item.code}</p>}
                          {item.manufacturer && <p className="text-xs text-gray-400">{item.manufacturer}</p>}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full">
                          {item.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="font-bold text-lg">{item.current_stock}</span>
                        <span className="text-gray-400 text-sm ml-1">{item.unit}</span>
                        {item.min_stock_level && (
                          <p className="text-xs text-gray-400">min: {item.min_stock_level}</p>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`px-2 py-1 text-xs rounded-full ${status.color}`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center text-sm">
                        {nextExpiry ? (
                          <div className="flex items-center justify-center gap-1">
                            <Calendar className="w-3 h-3 text-gray-400" />
                            <span>{formatDate(nextExpiry.expiry)}</span>
                            <span className="text-gray-400">({nextExpiry.quantity})</span>
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => openMovementModal(item, 'in')}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Carico"
                            data-testid={`load-stock-${item.id}`}
                          >
                            <ArrowDown className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openMovementModal(item, 'out')}
                            className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                            title="Scarico"
                            data-testid={`unload-stock-${item.id}`}
                          >
                            <ArrowUp className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openEditModal(item)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Modifica"
                            data-testid={`edit-item-${item.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteItem(item.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Elimina"
                            data-testid={`delete-item-${item.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Aggiungi/Modifica Prodotto */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {selectedItem ? 'Modifica Prodotto' : 'Nuovo Prodotto'}
              </h3>
              <button 
                onClick={() => { setShowAddModal(false); setSelectedItem(null); resetForm(); }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={selectedItem ? handleUpdateItem : handleAddItem} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome Prodotto *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500"
                    required
                    data-testid="product-name-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Codice/AIC</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500"
                    data-testid="product-code-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo *</label>
                  <select
                    value={formData.medication_type}
                    onChange={(e) => setFormData({...formData, medication_type: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500"
                    data-testid="product-type-select"
                  >
                    {MEDICATION_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Categoria *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500"
                    required
                    data-testid="product-category-select"
                  >
                    <option value="">Seleziona categoria</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Produttore</label>
                  <input
                    type="text"
                    value={formData.manufacturer}
                    onChange={(e) => setFormData({...formData, manufacturer: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unità di Misura</label>
                  <input
                    type="text"
                    value={formData.unit}
                    onChange={(e) => setFormData({...formData, unit: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500"
                    placeholder="pz, ml, flacone..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Soglia Scorta Minima</label>
                  <input
                    type="number"
                    value={formData.min_stock_level}
                    onChange={(e) => setFormData({...formData, min_stock_level: parseInt(e.target.value) || 0})}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500"
                    min="0"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prezzo Acquisto (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price_per_unit}
                    onChange={(e) => setFormData({...formData, price_per_unit: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Posizione Magazzino</label>
                  <input
                    type="text"
                    value={formData.storage_location}
                    onChange={(e) => setFormData({...formData, storage_location: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500"
                    placeholder="Es: Armadio A, Ripiano 2"
                  />
                </div>
                
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="requires_refrigeration"
                    checked={formData.requires_refrigeration}
                    onChange={(e) => setFormData({...formData, requires_refrigeration: e.target.checked})}
                    className="rounded text-sky-600"
                  />
                  <label htmlFor="requires_refrigeration" className="text-sm text-gray-700">
                    Richiede refrigerazione
                  </label>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500"
                  rows={2}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500"
                  rows={2}
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => { setShowAddModal(false); setSelectedItem(null); resetForm(); }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50"
                  data-testid="save-product-button"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  {selectedItem ? 'Aggiorna' : 'Salva'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Movimento */}
      {showMovementModal && selectedItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {movementData.movement_type === 'in' ? '📥 Carico' : '📤 Scarico'}: {selectedItem.name}
              </h3>
              <button 
                onClick={() => { setShowMovementModal(false); resetMovementForm(); }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleMovement} className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-500">Giacenza attuale</p>
                <p className="text-3xl font-bold text-gray-800">{selectedItem.current_stock} {selectedItem.unit}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo Movimento</label>
                <select
                  value={movementData.movement_type}
                  onChange={(e) => setMovementData({...movementData, movement_type: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500"
                  data-testid="movement-type-select"
                >
                  <option value="in">📥 Carico (entrata)</option>
                  <option value="out">📤 Scarico (uscita)</option>
                  <option value="adjustment">⚙️ Rettifica inventario</option>
                  <option value="expired">🗑️ Scaduto/Eliminato</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quantità *</label>
                <input
                  type="number"
                  value={movementData.quantity}
                  onChange={(e) => setMovementData({...movementData, quantity: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500"
                  min="1"
                  required
                  data-testid="movement-quantity-input"
                />
              </div>
              
              {movementData.movement_type === 'in' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Numero Lotto</label>
                    <input
                      type="text"
                      value={movementData.lot_number}
                      onChange={(e) => setMovementData({...movementData, lot_number: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500"
                      data-testid="lot-number-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Data Scadenza</label>
                    <input
                      type="date"
                      value={movementData.expiry_date}
                      onChange={(e) => setMovementData({...movementData, expiry_date: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500"
                      data-testid="expiry-date-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fornitore</label>
                    <input
                      type="text"
                      value={movementData.supplier}
                      onChange={(e) => setMovementData({...movementData, supplier: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Prezzo Unitario (€)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={movementData.unit_price}
                      onChange={(e) => setMovementData({...movementData, unit_price: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500"
                    />
                  </div>
                </>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Motivo/Note</label>
                <input
                  type="text"
                  value={movementData.reason}
                  onChange={(e) => setMovementData({...movementData, reason: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500"
                  placeholder={movementData.movement_type === 'out' ? 'Es: Utilizzato per paziente' : ''}
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => { setShowMovementModal(false); resetMovementForm(); }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg disabled:opacity-50 ${
                    movementData.movement_type === 'in' 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-orange-600 hover:bg-orange-700'
                  }`}
                  data-testid="confirm-movement-button"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Conferma
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Alert */}
      {showAlertsModal && alertsData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Alert Magazzino
              </h3>
              <button 
                onClick={() => setShowAlertsModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {alertsData.expired?.length > 0 && (
                <div>
                  <h4 className="font-medium text-red-700 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                    Prodotti Scaduti ({alertsData.expired.length})
                  </h4>
                  <div className="space-y-2">
                    {alertsData.expired.map((item, idx) => (
                      <div key={idx} className="bg-red-50 rounded-lg p-3 flex justify-between items-center">
                        <div>
                          <p className="font-medium">{item.name}</p>
                          <p className="text-sm text-gray-500">Lotto: {item.lot || '-'} • Scaduto: {formatDate(item.expiry)}</p>
                        </div>
                        <span className="text-red-700 font-medium">{item.quantity} pz</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {alertsData.expiring_soon?.length > 0 && (
                <div>
                  <h4 className="font-medium text-amber-700 mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    In Scadenza (90 giorni) ({alertsData.expiring_soon.length})
                  </h4>
                  <div className="space-y-2">
                    {alertsData.expiring_soon.map((item, idx) => (
                      <div key={idx} className="bg-amber-50 rounded-lg p-3 flex justify-between items-center">
                        <div>
                          <p className="font-medium">{item.name}</p>
                          <p className="text-sm text-gray-500">Lotto: {item.lot || '-'} • Scade: {formatDate(item.expiry)}</p>
                        </div>
                        <span className="text-amber-700 font-medium">{item.quantity} pz</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {alertsData.low_stock?.length > 0 && (
                <div>
                  <h4 className="font-medium text-orange-700 mb-3 flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Scorte Basse ({alertsData.low_stock.length})
                  </h4>
                  <div className="space-y-2">
                    {alertsData.low_stock.map((item, idx) => (
                      <div key={idx} className="bg-orange-50 rounded-lg p-3 flex justify-between items-center">
                        <div>
                          <p className="font-medium">{item.name}</p>
                          <p className="text-sm text-gray-500">Minimo: {item.min_level} pz</p>
                        </div>
                        <span className="text-orange-700 font-medium">{item.current_stock} pz</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {alertsData.low_stock?.length === 0 && alertsData.expiring_soon?.length === 0 && alertsData.expired?.length === 0 && (
                <div className="text-center py-8">
                  <Check className="w-12 h-12 mx-auto text-green-500 mb-4" />
                  <p className="text-gray-600">Nessun alert attivo. Magazzino in ordine!</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryManager;
